import type { FilterType } from '../dto/searchModels';
import { rangeQueryPattern } from '../util/constants';

export const parseBool = (val: string): boolean | null => {
  if (val === 'yes' || val === 'true') {
    return true;
  } else if (val === 'no' || val === 'false') {
    return false;
  }
  return null;
};


/**
 * Produces the final remaining string that contains all non filter strings
 * and invalid filters, if any.
 */
export const createFinalSearchString = (separatedFilters: SeparatedFilters): string => {
  const unRecognizedFilters: string [] = [];
  const startingSearchString = separatedFilters.remainingSearchString ? separatedFilters.remainingSearchString : '';
  for (const filter of separatedFilters.parsedFilters.filter(f => !f.isRecognized)) {
    unRecognizedFilters.push(`${filter.token}:${filter.value}`);
  }
  const finalRemaining = startingSearchString
  .concat(' ', unRecognizedFilters.join(' '))
  .trim()
  .replace(/\s+/g, ' ');
  return finalRemaining;
};

/**
 * Separates nested tree values.
 */
export const TREE_CHOICE_SEPARATOR = '▸';

/**
 * Represents a filter parsed from a search string.
 */
export interface ParsedFilter {
  isExactSearch: boolean;

  // The filter keyword. For example: "says" in the filter "says:foo".
  // In addition to the known and typed list of filter keywords, we
  // also support filters where the set of valid keys is
  // not statically known, such as mdf field id filters.
  token: string;
  value: string;
  isRecognized: boolean;
  isSticky: boolean;
  filterKey?: string;
}

/**
 * Holding all the parsed filters, and whatever was not matched as a filter.
 */
export interface SeparatedFilters {
  remainingSearchString: string;
  parsedFilters: ParsedFilter[];
}

/**
 * Checks whether tokenValue is surrounded by quotes.
 */
export function isQuotedText(tokenValue: string): boolean {
  const text = tokenValue.trim();
  if (text[0] === '\'' && text[text.length - 1] === '\'') return true;
  if (text[0] === '"' && text[text.length - 1] === '"') return true;
  return false;
}

/**
 * Separates quoted (") parts of a string and takes those parts
 * out of the string to get the non-quoted part.
 * @returns [Array of strings between quotes, String with the quoted parts cleaned]
 */
export function getQuotedAndNonQuotedPartsFromText(value: string): [string[], string] {
  const text = value?.trim();
  if (!text || (text && text.length < 1)) return [[], ''];
  const quoteRegex = /["](.*?)["]/g;
  const quotedParts = text.match(quoteRegex)?.map(val => val.replace(/["]/g, '').trim()) || [];
  const nonQuotedParts = text.replace(quoteRegex, '');

  return [quotedParts, nonQuotedParts];
}

/**
 * Takes in a string and returns the words that surround the given operator.
 * E.g. 'foo + bar baz'. 'foo' and 'bar' will be returned.
 */
export function getWordsSurroundingOperator(value: string, operator: string, operatorSynonym?: string): string[] {
  if (!value || (value && value.length < 1)) return [];
  let stringToMatch = value;
  if (operatorSynonym) {
    const regexToReplaceSynonym = new RegExp(`(\\s)\\b${operatorSynonym}\\b(\\s)`, 'g');
    stringToMatch = stringToMatch.replace(regexToReplaceSynonym, ` ${operator} `);
  }
  const beforeAndAfterRegex = new RegExp(`(\\S+\\s+|)[${operator}](\\s+\\S+|)`, 'g');
  let wordsSurroundingOperator: string[] = [];
  // RegExp.exec() is not equivalent to string.match() when the expression has the global flag
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const stringsContainingOperator = stringToMatch.match(beforeAndAfterRegex) || [];
  for (const val of stringsContainingOperator) {
    const words = [];
    for (const word of val.replace(operator, '').split(' ')) {
      const trimmedWord = word.trim();
      if (trimmedWord.length > 0) {
        words.push(trimmedWord);
      }
    }
    wordsSurroundingOperator = wordsSurroundingOperator.concat(words);
  }
  return wordsSurroundingOperator;
}



/**
 * Creates the regex used to parse token:value from strings.
 */
export function buildFilterRegex(withQuotes = false): RegExp {
  const quotedSequence = '["][^\'"]+["]';
  const validFilterNameRegex =
    '[.a-zA-Z0-9#_-]+(' +
    // For events we support quoting the label,  as it may include spaces: 'events."schema label".fieldId'
    quotedSequence +
    ')*[.a-zA-Z0-9#_-]*';
  const validFilterValueRegex = [
    `${rangeQueryPattern}`,
    '([^"\'\\s]+)',
    `(${quotedSequence}(\\|sticky)?)`,
    '([\'][^\'"]+[\'](\\|sticky)?)',
  ].join('|');
  let filterRegex = `${validFilterNameRegex}( *):( *)(${validFilterValueRegex})`;
  if (withQuotes) {
    filterRegex = `["']( *)${filterRegex}( *)["']`;
  }
  return RegExp(filterRegex, 'g');
}

export function getFiltersFromSearchString(searchString: string) {
  // searching for "type:file" (with the quotes) should be treated as an exact search
  // instead of interpreting it as a possible filter
  const exactSearchWithColonRegex = buildFilterRegex(true);
  let searchStringWithoutExactSearchWithColon = searchString;

  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const exactSearchWithColonMatches = searchString.match(exactSearchWithColonRegex);
  if (exactSearchWithColonMatches) {
    for (const match of exactSearchWithColonMatches) {
      searchStringWithoutExactSearchWithColon = searchStringWithoutExactSearchWithColon.replace(match, '');
    }
  }

  const filterRegex = buildFilterRegex();
  // RegExp.exec() is not equivalent to string.match() when the expression has the global flag
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const possibleFilters = searchStringWithoutExactSearchWithColon.match(filterRegex);

  return possibleFilters;
}

export function removeFiltersFromString(possibleFilters: RegExpMatchArray, searchString: string) {

  for (const filter of possibleFilters) {
    searchString = searchString.replace(filter, '');
  }

  searchString = searchString.trim().replace(/\s+/g, ' ');
  return searchString;
}





/**
 * Encodes string with special characters into URI format
 * @param stringWithCharacters Problematic string that contains spaces, quotes and other special characters
 */
export function encodeStringAsUri(stringWithCharacters: string): string {
  return encodeURI(stringWithCharacters).replace(/'/g, '%27'); // Encode single quote(') with %27 as encodeURI method doesn't encode it
}


/**
 * Given a value for startDate, returns array of [startDate, endDate]
 * Makes sure existing endDate isn't greater than startDate and if it's
 * not defined, returns current date
 * @param rangeEnd
 */
export const getRangeStartDateArray = (date: Date | null | undefined, rangeEnd: Date | null | undefined) => {
  const dateArray: Date[] = [];
  if (date) {
    dateArray.push(date);
    if (rangeEnd) {
      const normalized = rangeEnd >= date ? rangeEnd : date;
      dateArray.push(normalized);
    } else {
      dateArray.push(date);
    }
  }
  return dateArray;
};

/**
 * Given a value for endDate, returns array of [startDate, endDate]
 * Makes sure existing startDate isn't smaller than endDate and if it's
 * not defined, sets it to be the same as endDate
 * @param rangeEnd
 */
export const getRangeEndDateArray = (date: Date | null | undefined, rangeStart: Date | null | undefined) => {
  const dateArray: Date[] = [];
  if (date) {
    if (rangeStart) {
      const normalized = rangeStart <= date ? rangeStart : date;
      dateArray.push(normalized);
    } else {
      dateArray.push(date);
    }
    dateArray.push(date);
  }
  return dateArray;
};

/**
 * Create date array supporting single dates (from-only or to-only)
 * Returns [startDate, endDate] where either can be null for open ranges
 */
export const getFlexibleDateArray = (startDate: Date | null | undefined, endDate: Date | null | undefined) => {
  const dateArray: (Date | null)[] = [];

  if (startDate || endDate) {
    // Ensure order is maintained if both dates are provided
    if (startDate && endDate) {
      if (startDate <= endDate) {
        dateArray.push(startDate, endDate);
      } else {
        dateArray.push(endDate, startDate);
      }
    } else {
      // Single date - allow null for open ranges
      dateArray.push(startDate || null, endDate || null);
    }
  }

  return dateArray;
};

/**
 * Update date array for start date changes, supporting flexible ranges
 */
export const getFlexibleRangeStartArray = (date: Date | null | undefined, currentEnd: Date | null | undefined) => {
  return getFlexibleDateArray(date, currentEnd);
};

/**
 * Update date array for end date changes, supporting flexible ranges
 */
export const getFlexibleRangeEndArray = (date: Date | null | undefined, currentStart: Date | null | undefined) => {
  return getFlexibleDateArray(currentStart, date);
};

// Maps the field of a search hit to the corresponding filter key.
// TODO: Should map to FilterType instead of the specific StaticFilterKeywords.
export function searchHitFieldToFilter(field: string): FilterType | undefined {
  return searchHitFieldToFilterMapping[field];
}

export function supportsSameTime(filter: FilterType) {
  return filtersWithSameTimeSupport.has(filter);
}

const searchHitFieldToFilterMapping: Record<string, FilterType> = {
  'detectedPersons': 'filter:person',
  'events': 'filter:events',
  'detectedCelebrities' : 'filter:person',
  'timedTranscript': 'filter:transcript',
  'timedTranslatedTranscript' : 'filter:timedTranslatedTranscript',
  'detectedLabels' : 'filter:label',
};

// The filter types that support "same time" search.
const filtersWithSameTimeSupport = new Set<FilterType>([
  'filter:label',
  'filter:person',
  'filter:transcript',
  'filter:timedTranslatedTranscript',
  'filter:events',
]);
